import { useMemo } from 'react';

import type { GridOptions, ICellRendererParams } from 'ag-grid-enterprise';

import { ROW_HEIGHT } from '@/constants/grid';

import type { DefaultGridOptionsOptions } from './types/default-grid-options.options';

/**
 * Custom hook that returns default grid options for AG Grid, tailored for report views.
 *
 * @template TData - The type of data rows in the grid.
 * @returns {GridOptions<TData>} The default grid options including cell selection, custom date formatting/parsing,
 *          charting, and a status bar with multiple panels.
 *
 * @remarks
 * - The hook memoizes the grid options for performance.
 * - Includes a custom `date` data type definition with value formatting and parsing logic,
 *   supporting Golang `time.Time` string format (e.g., "2025-04-14T00:00:00Z").
 * - Enables charting and provides a status bar with row count and aggregation panels.
 */
export const useDefaultGridOptions = <TData = unknown>(options?: DefaultGridOptionsOptions) => {
  const { hasGrandTotals = true, hasStatusBar = true, rowModel = 'clientSide' } = options ?? {};

  return useMemo(() => {
    let options: GridOptions<TData> = {
      animateRows: false,
      autoGroupColumnDef: {
        cellRendererParams: {
          totalValueGetter: (params: ICellRendererParams<TData>) => {
            const isRootLevel = params.node.level === -1;

            if (isRootLevel) {
              return 'Grand Total'; // Custom label for grand total
            }

            return `Sub Total (${params.value})`; // Custom label for group total
          },
        },
      },
      cellSelection: true,
      dataTypeDefinitions: {
        date: {
          baseDataType: 'date',
          extendsDataType: 'date',
          valueParser: ({ newValue }) => {
            const fallback = new Date();
            // convert from Golang time.Time format (2025-04-14T00:00:00Z) to a Date object
            const dateParts = newValue.split('T')[0]?.split('-') ?? [];

            const year = Number.parseInt(dateParts[0] ?? fallback.getFullYear().toString(), 10);
            const month = Number.parseInt(dateParts[1] ?? (fallback.getMonth() + 1).toString(), 10) - 1;
            const day = Number.parseInt(dateParts[2] ?? fallback.getDate().toString(), 10);

            return dateParts.length === 3 ? new Date(year, month, day) : null;
          },
        },
      },
      enableCharts: true,
      getRowHeight: (params) => {
        if (params.node.rowPinned) {
          return ROW_HEIGHT;
        }

        return ROW_HEIGHT;
      },
      groupDefaultExpanded: 1,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 25, 50, 100],
      suppressAggFuncInHeader: true,
    };

    if (hasGrandTotals) {
      options = {
        ...options,
        grandTotalRow: 'bottom',
        groupTotalRow: 'bottom',
      };
    }

    if (hasStatusBar) {
      options = {
        ...options,
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalAndFilteredRowCountComponent' },
            { statusPanel: 'agTotalRowCountComponent' },
            { statusPanel: 'agFilteredRowCountComponent' },
            { statusPanel: 'agSelectedRowCountComponent' },
            { statusPanel: 'agAggregationComponent' },
          ],
        },
      };
    }

    switch (rowModel) {
      case 'clientSide':
        options = {
          ...options,
          rowModelType: 'clientSide',
        };
        break;

      case 'hybrid':
        options = {
          ...options,
          defaultColDef: {
            ...options.defaultColDef,
            // don't let AG Grid sort locally
            comparator: () => {
              return 0;
            },
            filterParams: {
              // don't let AG Grid filter locally
              textMatcher: () => {
                return true;
              },
            },
          },
        };
        break;
    }

    return options;
  }, [hasGrandTotals, hasStatusBar, rowModel]);
};
